'use client';

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useScrollLock } from '@akademi/ui';
import { useBlockControlStamp } from '@akademi/site-builder-runtime';
import { CloseButton } from './popup/chrome';

/**
 * "Discuss in cohort" stub modal. Posts a shareable deep link to the
 * picked cohort via the host-supplied {@code onSend} callback (real
 * implementations may shell out to Slack/Teams or persist a thread
 * in the host backend).
 *
 * <p>The cohort list is operator-supplied — when no cohorts are
 * configured the modal shows a "copy link" fallback so facilitators
 * can paste manually.
 */
export interface MicroCohortDiscussModalProps {
  open: boolean;
  onClose: () => void;
  contentTitle: string;
  shareUrl: string;
  cohorts?: Array<{ id: string | number; name: string }>;
  onSend?: (args: {
    cohortId: string | number;
    note: string;
    shareUrl: string;
  }) => Promise<void> | void;
}

export function MicroCohortDiscussModal({
  open,
  onClose,
  contentTitle,
  shareUrl,
  cohorts = [],
  onSend,
}: MicroCohortDiscussModalProps) {
  const { t } = useTranslation('site-builder-blocks');
  const [mounted, setMounted] = useState(false);
  const [cohortId, setCohortId] = useState<string | number | ''>('');
  const [note, setNote] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'copied'>('idle');
  // Portals to document.body — stamp its own controls (see MediaDetailModal).
  const stampRef = useBlockControlStamp<HTMLDivElement>({ portal: true });

  useEffect(() => setMounted(true), []);

  useScrollLock(open);

  if (!open || !mounted) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setStatus('copied');
      setTimeout(() => setStatus('idle'), 2000);
    } catch {
      /* clipboard rejected */
    }
  };

  const send = async () => {
    if (!onSend || !cohortId) return;
    setStatus('sending');
    try {
      await onSend({ cohortId, note, shareUrl });
      setStatus('sent');
    } catch {
      setStatus('idle');
    }
  };

  const node = (
    <div
      ref={stampRef}
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.65)',
        backdropFilter: 'blur(4px)',
        zIndex: 9999,
        display: 'grid',
        placeItems: 'center',
        padding: 'clamp(8px, 2vw, 24px)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 500,
          background: 'var(--color-surface-card, #fff)',
          borderRadius: 12,
          padding: 24,
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <CloseButton onClose={onClose} />
        <div>
          <h2 style={{ margin: 0, fontSize: 'var(--font-size-lg, 18px)', fontWeight: 600 }}>
            {t('microCohortDiscuss.title')}
          </h2>
          <p
            style={{
              margin: 'var(--space-1, 4px) 0 0',
              fontSize: '13px',
              color: 'var(--color-text-secondary, #71717a)',
            }}
          >
            <Trans
              t={t}
              i18nKey="microCohortDiscuss.intro"
              values={{ title: contentTitle }}
              components={{ b: <strong /> }}
            />
          </p>
        </div>

        {cohorts.length > 0 ? (
          <>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 'var(--font-size-xs, 12px)', fontWeight: 600 }}>
                {t('microCohortDiscuss.cohort')}
              </span>
              <select
                value={cohortId}
                onChange={(e) => setCohortId(e.target.value)}
                style={{
                  padding: 'var(--space-2, 8px) var(--space-3, 12px)',
                  border: '1px solid var(--color-border, rgba(0,0,0,0.12))',
                  borderRadius: 6,
                }}
              >
                <option value="">{t('microCohortDiscuss.select')}</option>
                {cohorts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={{ fontSize: 'var(--font-size-xs, 12px)', fontWeight: 600 }}>
                {t('microCohortDiscuss.note')}
              </span>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder={t('microCohortDiscuss.notePlaceholder')}
                style={{
                  padding: 10,
                  border: '1px solid var(--color-border, rgba(0,0,0,0.12))',
                  borderRadius: 6,
                  fontSize: 'var(--font-size-sm, 14px)',
                  fontFamily: 'var(--font-family-body, inherit)',
                  resize: 'vertical',
                }}
              />
            </label>
            <button
              type="button"
              onClick={send}
              disabled={!cohortId || status === 'sending'}
              style={{
                padding: 'var(--space-2-5, 10px) var(--space-5, 20px)',
                border: 'none',
                borderRadius: 8,
                background: 'var(--color-text-primary, #18181b)',
                color: 'var(--color-text-on-brand, #fff)',
                fontSize: 'var(--font-size-sm, 14px)',
                fontWeight: 600,
                cursor: cohortId ? 'pointer' : 'not-allowed',
                opacity: cohortId ? 1 : 0.5,
                alignSelf: 'flex-start',
              }}
            >
              {status === 'sending'
                ? t('microCohortDiscuss.sending')
                : status === 'sent'
                  ? t('microCohortDiscuss.sent')
                  : t('microCohortDiscuss.send')}
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={copy}
            style={{
              padding: 'var(--space-2-5, 10px) var(--space-5, 20px)',
              border: '1px solid var(--color-border, rgba(0,0,0,0.12))',
              borderRadius: 8,
              background: 'transparent',
              fontSize: 'var(--font-size-sm, 14px)',
              fontWeight: 600,
              cursor: 'pointer',
              alignSelf: 'flex-start',
            }}
          >
            {status === 'copied'
              ? t('microCohortDiscuss.copied')
              : t('microCohortDiscuss.copyLink')}
          </button>
        )}
      </div>
    </div>
  );

  return createPortal(node, document.body);
}
